import { Contact } from "./type";

export function cleanContacts(contacts: Contact[]) {
  return contacts
    .filter((contact) => {
      // Ensure phoneNumber exists and is a valid Iranian cell phone number
      return (
        contact.phoneNumber &&
        /^(\+98|0)?9\d{9}$/.test(contact.phoneNumber.replace(/\s+/g, ""))
      );
    })
    .map((contact) => {
      // Remove spaces and convert +98 to 0 in phone numbers
      contact.phoneNumber =
        contact?.phoneNumber &&
        contact?.phoneNumber
          .replace(/\s+/g, "") // Remove spaces
          .replace(/[()]/g, "") // Remove parentheses
          .replace(/^\+98/, "0"); // Convert +98 to 0
      return contact;
    });
}

// --- Decode Quoted Printable (UTF-8) ---
export const decodeQuotedPrintable = (input: string) => {
  // 1) حذف soft break ها
  const qp = input.replace(/=\r?\n/g, "");

  // 2) تبدیل =XX به بایت
  const buf: number[] = [];
  let i = 0;

  while (i < qp.length) {
    if (
      qp[i] === "=" &&
      /[A-Fa-f0-9]/.test(qp[i + 1]) &&
      /[A-Fa-f0-9]/.test(qp[i + 2])
    ) {
      const hex = qp.substring(i + 1, i + 3);
      buf.push(parseInt(hex, 16));
      i += 3;
    } else {
      buf.push(qp.charCodeAt(i));
      i++;
    }
  }

  // 3) تبدیل بایت‌ها به UTF-8
  try {
    return new TextDecoder("utf-8").decode(new Uint8Array(buf));
  } catch {
    return qp; // fallback
  }
};

// --- Internal helpers for vCard parsing ---
type VCardParams = Record<string, string>;

const clean = (s: string) =>
  s ? s.replace(/\s+/g, " ").replace(/^[\s.]+|[\s.]+$/g, "") : "";

function parseVCardProp(
  line: string
): { name: string; params: VCardParams; value: string } | null {
  const idx = line.indexOf(":");
  if (idx === -1) return null;
  const lhs = line.slice(0, idx);
  const value = line.slice(idx + 1);
  const [rawName, ...paramParts] = lhs.split(";");
  const name = rawName.toUpperCase();
  const params: VCardParams = {};
  for (const p of paramParts) {
    const [k, v] = p.split("=");
    if (k) params[k.toUpperCase()] = (v || "").toUpperCase();
  }
  return { name, params, value };
}

function decodeValueByParams(value: string, params: VCardParams): string {
  const encoding = params["ENCODING"];
  if (encoding === "QUOTED-PRINTABLE") {
    return clean(decodeQuotedPrintable(value));
  }
  return clean(value);
}

function extractNameFromBlock(block: string): {
  firstName: string;
  lastName: string;
  displayName: string;
} {
  // Multiline safe
  const nMatch = block.match(/^[ \t]*N(?:;[^:]*)?:(.*)$/im);
  const fnMatch = block.match(/^[ \t]*FN(?:;[^:]*)?:(.*)$/im);

  let firstName = "";
  let lastName = "";
  let displayName = "";

  if (nMatch) {
    // Rebuild full N line (with params) to decode correctly
    const nLineFull =
      (block.match(/^[ \t]*N[^\n\r]*$/im) || [])[0] || nMatch[0];
    const parsed = parseVCardProp(nLineFull);
    const decoded = parsed
      ? decodeValueByParams(nMatch[1], parsed.params)
      : clean(nMatch[1]);
    // N: Family;Given;Additional;Prefix;Suffix
    const [family = "", given = ""] = decoded.split(";");
    firstName = clean(given);
    lastName = clean(family);
  }

  if (fnMatch) {
    const fnLineFull =
      (block.match(/^[ \t]*FN[^\n\r]*$/im) || [])[0] || fnMatch[0];
    const parsed = parseVCardProp(fnLineFull);
    const decoded = parsed
      ? decodeValueByParams(fnMatch[1], parsed.params)
      : clean(fnMatch[1]);
    displayName = decoded;
    // Fallback to split when N was missing
    if (!firstName && !lastName && decoded) {
      const parts = decoded.split(/\s+/).filter(Boolean);
      if (parts.length === 1) {
        firstName = parts[0];
      } else if (parts.length > 1) {
        firstName = parts[0];
        lastName = parts.slice(1).join(" ");
      }
    }
  }

  // Ensure displayName has a value
  if (!displayName) {
    displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
  }

  return { firstName, lastName, displayName };
}

function extractPhoneFromBlock(block: string): string {
  // get first TEL (any type)
  const telMatch = block.match(/^[ \t]*TEL[^:]*:(.*)$/im);
  const phone = telMatch ? clean(telMatch[1]) : "";
  // remove spaces from phone number
  return phone.replace(/\s+/g, "").replace(/[()]/g, "");
}

// Public API: parse VCF text into Contact[]
export function parseVcfContacts(text: string): Contact[] {
  const cards = text.split(/BEGIN:VCARD/i).slice(1);
  const results: Contact[] = [];

  for (const raw of cards) {
    const block = raw.split(/END:VCARD/i)[0] || raw;
    const { firstName, lastName, displayName } = extractNameFromBlock(block);
    const phoneNumber = extractPhoneFromBlock(block);

    results.push({
      firstName,
      lastName,
      displayName,
      phoneNumber,
      checked: false,
    });
  }

  return results;
}
